import domtoimage from 'dom-to-image';
import { saveAs } from 'file-saver';
import { effectiveBackgroundColor } from 'utils/exportImage';
import { Button, Flex, Space } from "antd";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFileExcel, faFileImage } from '@fortawesome/pro-light-svg-icons';
import { FishboneBranch, FishboneItem } from 'graph/dist/generated';
import * as ExcelJS from 'exceljs';
import { flatten, minBy, maxBy, sortBy } from 'lodash';
import { getFishboneBranchCompletion, getFishboneBranchEndOn, getFishboneBranchStartOn, getFishboneBranchTrend } from 'utils/rules';
import { useTranslation } from 'react-i18next';


const generateImage = () => {
    const node = document.querySelector('.ant-layout.Fishbone');
    const nodeWidth = node.clientWidth - 300;

    const calculatedHeight = (node.clientHeight) * 2
    domtoimage.toBlob(node, {
        bgcolor: effectiveBackgroundColor(node),
        height: calculatedHeight,
        width: (nodeWidth) * 2,
        quality: 1,
        filter: (node) => {
            if (!node.className || !node.className.match)
                return true;

            // Avoid classnames containing['ActionButton', 'ant-btn']
            return !node.className.match('ActionsWrapper') && !node.className.match('ant-btn');
        },
        style: {
            transform: "scale(" + 2 + ")",
            marginTop: 0 + "px",
            transformOrigin: `0% 0%`,

            height: calculatedHeight,
            width: (nodeWidth) * 2 * 1.2,
            backgroundColor: "transparent",
            backgroundImage: "url(data:image/svg+xml;base64,PD94bWwgdmVyc2lvbj0iMS4wIiBlbmNvZGluZz0idXRmLTgiPz4KPHN2ZyB2ZXJzaW9uPSIxLjEiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyIgeG1sbnM6eGxpbms9Imh0dHA6Ly93d3cudzMub3JnLzE5OTkveGxpbmsiIHg9IjBweCIgeT0iMHB4IgoJIHZpZXdCb3g9IjAgMCAxNjYgMTQuMiIgeG1sOnNwYWNlPSJwcmVzZXJ2ZSI+CjxzdHlsZSB0eXBlPSJ0ZXh0L2NzcyI+Cgkuc3Qwe2ZpbGw6I0ZGRkZGRjsgZm9udC1mYW1pbHk6J0FyaWFsTVQnLCBzYW5zLXNlcmlmOyBmb250LXNpemU6MTJweDt9Cjwvc3R5bGU+Cjx0ZXh0IGNsYXNzPSJzdDAiPkdlcm9zaXR1c8KuIC0gQWxsIHJpZ2h0cyByZXNlcnZlZDwvdGV4dD4KPC9zdmc+)",
            backgroundRepeat: 'no-repeat',
            backgroundSize: '167.5px 167.5px',
            backgroundPositionX: `${nodeWidth / 2 + 15}px`,
            backgroundPositionY: `${calculatedHeight / 2 - 90}px`,
        }
    })
        .then(function (blob) {

            // Current date
            saveAs(blob, `Gerositus-${new Date().toLocaleDateString().replace(/\//g, '-') + '-' +
                new Date().toLocaleTimeString().replace(/:/g, '-')
                }.png`);
        });
}

const generateExcel = async (fishboneBranchs: FishboneBranch[], fishboneDescription: string, fishboneStartOn: string, fishboneEndOn: string) => {

    const sortedBranchs = sortBy(fishboneBranchs, [getFishboneBranchStartOn, getFishboneBranchEndOn, "reference", "name"])

    const prepareGeneralRow = (fishboneBranch: FishboneBranch) => {

        const minDate = getFishboneBranchStartOn(fishboneBranch) ? new Date(getFishboneBranchStartOn(fishboneBranch)) : null
        const maxDate = getFishboneBranchEndOn(fishboneBranch) ? new Date(getFishboneBranchEndOn(fishboneBranch)) : null

        return [
            fishboneBranch.reference,
            fishboneBranch.name,
            fishboneBranch.description,
            fishboneBranch.weight,
            fishboneBranch.responsible,
            minDate,
            maxDate,
            getFishboneBranchCompletion(fishboneBranch) !== null ? getFishboneBranchCompletion(fishboneBranch) / 100 : null,
            getFishboneBranchTrend(fishboneBranch) !== null ? getFishboneBranchTrend(fishboneBranch) / 100 : null
        ]
    }

    const branchsWeight = sortedBranchs.reduce((a, b) => a + b.weight, 0);

    const prepareFinalRow = () => {

        let minStartBranch = minBy(sortedBranchs, f => getFishboneBranchStartOn(f))
        let maxEndBranch = maxBy(sortedBranchs, f => getFishboneBranchEndOn(f))

        const averageCompletion = sortedBranchs.map(b => getFishboneBranchCompletion(b)).reduce((a, b) => a + b, 0) / (branchsWeight || 1)
        const averageTrend = sortedBranchs.map(b => getFishboneBranchTrend(b)).reduce((a, b) => a + b, 0) / (branchsWeight || 1)

        return [
            " ",
            " ",
            " ",
            " ",
            " ",
            // Min of any start date
            minStartBranch && getFishboneBranchStartOn(minStartBranch) ? new Date(getFishboneBranchStartOn(minStartBranch)) : null,
            // Max of any end date
            maxEndBranch && getFishboneBranchEndOn(maxEndBranch) ? new Date(getFishboneBranchEndOn(maxEndBranch)) : null,
            // Average comp letion
            averageCompletion !== null ? averageCompletion / 100 : null,
            // Average trend
            averageTrend !== null ? averageTrend / 100 : null,
        ]
    }

    const prepareDetailRow = (fishboneItem: FishboneItem) => {
        return [
            fishboneItem.reference,
            fishboneItem.name,
            fishboneItem.description,
            fishboneItem.weight,
            fishboneItem.responsible,
            fishboneItem.startOn ? new Date(fishboneItem.startOn) : null,
            fishboneItem.endOn ? new Date(fishboneItem.endOn) : null,
            fishboneItem.completion !== null ? fishboneItem.completion / 100 : null,
            fishboneItem.trend !== null ? fishboneItem.trend / 100 : null,
        ]
    }

    const columns = [
        { header: 'Reference', key: 'reference', width: 15 },
        { header: 'Name', key: 'name', width: 50 },
        { header: 'Description', key: 'description', width: 80 },
        { header: 'Weight', key: 'weight', width: 18 },
        { header: 'Responsible', key: 'responsible', width: 18 },
        { header: 'Start', key: 'startOn', width: 18 },
        { header: 'End', key: 'endOn', width: 32 },
        { header: 'Completion', key: 'completion', width: 18 },
        { header: 'Status', key: 'trend', width: 18 },
    ];

    const rows = flatten(sortedBranchs.map(b => [
        { main: true, data: prepareGeneralRow(b) },
        ...sortBy(b.items, "startOn", "endOn", "reference", "name").map(i => ({ main: false, data: prepareDetailRow(i) }))
    ]));

    const workbook = new ExcelJS.Workbook();
    workbook.created = new Date();

    const sheet = workbook.addWorksheet('Fishbone');
    sheet.columns = columns;
    sheet.autoFilter = {
        from: 'A1',
        to: 'J1',
    }

    // Format the header
    sheet.getRow(1)
        .fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: 'FFDDDDDD' },
    };

    sheet.getRow(1).font = {
        bold: true,
        size: 16
    };

    sheet.getRow(1).eachCell((cell, cellIndex) => {

        // Dates => format as dd/mm/yyyy
        if (cellIndex === 6 || cellIndex === 7) {
            cell.numFmt = 'dd/mm/yyyy';
        }
    });

    rows.forEach((rowData, index) => {

        const row = sheet.getRow(index + 2)
        row.values = rowData.data;

        row.eachCell((cell, cellIndex) => {


            if (rowData.main) {

                row.fill = {
                    type: 'pattern',
                    pattern: 'solid',
                    fgColor: { argb: 'FFF0F0F0' },
                };

                cell.font = {
                    bold: true,
                    size: 16
                }

            } else {
                cell.font = {
                    size: 14
                }
            }

            // Trend => format as percentage
            if (cellIndex > 7) {
                cell.numFmt = '0%';
            }

            // G, H, E are dates
            if (cellIndex === 6 || cellIndex === 7) {
                cell.numFmt = 'dd/mm/yyyy';
            }

        })
    });

    const finalRow = sheet.getRow(rows.length + 2)
    finalRow.values = prepareFinalRow()
    finalRow.fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: 'FFDDDDDD' },
    };

    finalRow.eachCell((cell, cellIndex) => {

        cell.font = {
            bold: true,
            size: 16
        }

        if (cellIndex > 7) {
            cell.numFmt = '0%';
        }

        if (cellIndex === 6 || cellIndex === 7) {
            cell.numFmt = 'dd/mm/yyyy';
        }
    })

    const buffer = await workbook.xlsx.writeBuffer();

    saveAs(new Blob([buffer], { type: "application/octet-stream" }), 'Gerositus-Risks.xlsx');

}

export const ExportTab = ({ fishboneBranchs, fishboneDescription, fishboneStartOn, fishboneEndOn }) => {

    const { t } = useTranslation();

    return (

        <Flex vertical className="mt-05">
            {<Button className="mb-1" variant="outlined" onClick={() => generateExcel(fishboneBranchs, fishboneDescription, fishboneStartOn, fishboneEndOn)}>
                <FontAwesomeIcon icon={faFileExcel} />
                {t('actions.export.excel')}
            </Button>}


            <Button onClick={() => generateImage()}>
                <Space>
                    <FontAwesomeIcon icon={faFileImage} />
                    {t("actions.export.image")}
                </Space>
            </Button>
        </Flex >
    );
}